// ============================================================
// Student Fee Status Detail Page
// ============================================================
// Dedicated page displaying rich tabular views for both
// Installments Schedule and Payment History of a student.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Chip, Skeleton, Alert, IconButton, Tabs, Tab, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarColumnsButton, GridToolbarFilterButton } from '@mui/x-data-grid';
import { ArrowLeft, CalendarDays, Receipt, Eye, Ban, X } from 'lucide-react';
import { PageHeader } from '@/components/navigation/PageHeader';

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ p: 1, gap: 1 }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
}
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tokens } from '@/theme/tokens';
import { useSchool } from '@/hooks/useSchool';
import { useAuth } from '@/hooks/useAuth';
import { studentsApi } from '@/api/students.api';
import { feeReceiptsApi } from '@/api/feeReceipts.api';
import { useStudentFeeSummary, useStudentInstallments } from '@/hooks/useStudentFeeInstallments';
import { useStudentReceipts, useCancelFeeReceipt } from '@/hooks/useFeeReceipts';
import { ReceiptModal } from './components/ReceiptModal';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';

// ── Currency Formatter ─────────────────────────────────────────
const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(Number(val))) return '₹0';
  const num = Number(val);
  const hasDecimals = Math.abs(num % 1) > 0.0001;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(num);
};

export function StudentFeeStatusDetailPage() {
  usePageMeta('Student Fee Status Details', [
    { label: 'Fees', path: '/fees' },
    { label: 'Collect Fee', path: '/fees/collect' },
    { label: 'Status Details' },
  ]);

  const { studentId } = useParams();
  const navigate = useNavigate();
  const { currentSchool, schoolId } = useSchool();
  const { user } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const getActiveTabFromQuery = useCallback(() => {
    const tabParam = (searchParams.get('tab') || location.state?.tab || '').toLowerCase();
    if (tabParam === '1' || tabParam === 'receipts' || tabParam === 'payment-history' || tabParam === 'history') {
      return 1;
    }
    return 0;
  }, [searchParams, location.state]);

  const [student, setStudent] = useState(null);
  const [isLoadingStudent, setIsLoadingStudent] = useState(true);
  const [statusTab, setStatusTab] = useState(getActiveTabFromQuery);
  const [receiptData, setReceiptData] = useState(null);

  useEffect(() => {
    setStatusTab(getActiveTabFromQuery());
  }, [getActiveTabFromQuery]);

  const handleTabChange = (_, newValue) => {
    setStatusTab(newValue);
    setSearchParams(newValue === 1 ? { tab: 'payment-history' } : { tab: 'installments' }, { replace: true });
  };

  // ── API Hooks ────────────────────────────────────────────────
  const {
    data: feeSummary,
    isLoading: isLoadingSummary,
  } = useStudentFeeSummary(studentId, { enabled: !!studentId });

  const {
    data: installments = [],
    isLoading: isLoadingInstallments,
    refetch: refetchInstallments,
  } = useStudentInstallments(studentId, { enabled: !!studentId });

  const { data: receipts = [], isLoading: isLoadingReceipts, refetch: refetchReceipts } = useStudentReceipts(studentId, { enabled: !!studentId });

  // Cancel receipt mutation
  const [cancelReceiptRow, setCancelReceiptRow] = useState(null);
  const { mutate: cancelReceipt, isPending: isCancelling } = useCancelFeeReceipt();

  const handleConfirmCancelReceipt = () => {
    if (!cancelReceiptRow?.id) return;
    cancelReceipt(cancelReceiptRow.id, {
      onSuccess: () => {
        toast.success(`Fee Receipt ${cancelReceiptRow.receipt_no || cancelReceiptRow.receipt_number || ''} cancelled successfully! Amount reversed.`);
        setCancelReceiptRow(null);
        refetchReceipts();
        refetchInstallments();
        refetchSummary();
      },
      onError: (err) => {
        const msg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to cancel receipt';
        toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      },
    });
  };

  // ── Load student details ─────────────────────────────────────
  useEffect(() => {
    if (schoolId && studentId) {
      setIsLoadingStudent(true);
      studentsApi.getById(schoolId, studentId)
        .then((res) => setStudent(res?.data || res))
        .catch((err) => {
          console.error('Failed to load student details:', err);
          setStudent(null);
        })
        .finally(() => setIsLoadingStudent(false));
    }
  }, [schoolId, studentId]);

  // ── Payment progress % ───────────────────────────────────────
  const paymentProgress = useMemo(() => {
    if (!feeSummary || !feeSummary.total_fee) return 0;
    return Math.min(100, Math.round((feeSummary.paid / feeSummary.total_fee) * 100));
  }, [feeSummary]);

  // ── Installment status helper ────────────────────────────────
  const getStatusColor = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'PAID') return 'success';
    if (s === 'OVERDUE' || s === 'PENDING') return 'error';
    if (s === 'PARTIAL') return 'warning';
    return 'default';
  };

  // ── Preview receipt handler ──────────────────────────────────
  const handlePreviewReceipt = useCallback(async (receiptRow) => {
    const stu = student?.student || student || {};
    const enrollment = student?.enrollment || {};
    const studentName = `${stu?.first_name || ''} ${stu?.middle_name || ''} ${stu?.last_name || ''}`.replace(/\s+/g, ' ').trim() || stu?.student_name || 'Student';
    const admissionNo = stu?.admission_no || stu?.admission_number || stu?.student_id || '-';
    const fatherName = stu?.father_name || stu?.parent?.father_name || stu?.guardian_name || '-';
    const className = (enrollment?.class?.class_name || stu?.class_name || '').replace(/^Class\s+/i, '');
    const sectionName = enrollment?.section?.section_name || stu?.section_name || '';

    // Create installment lookup map
    const instMap = new Map((installments || []).map((i) => [i.id, i]));

    const enrichItems = (rawItems) => {
      if (!Array.isArray(rawItems) || rawItems.length === 0) return [];
      return rawItems.map((item) => {
        const inst = item.student_fee_installment_id ? instMap.get(item.student_fee_installment_id) : null;
        const description =
          item.installment_name ||
          item.installmentName ||
          inst?.installment_name ||
          item.fee_type_name ||
          item.feeTypeName ||
          inst?.fee_type?.name ||
          'Installment Fee';

        const discount = Number(item.discount_amount ?? item.discount ?? 0);
        const fine = Number(item.fine_amount ?? item.fine ?? 0);
        const paid = Number(item.paid_amount ?? item.amount_paid ?? item.paying ?? item.amount ?? 0);
        const feeAmount = Number(
          item.fee_amount ??
          item.feeAmount ??
          item.original_amount ??
          inst?.balance_amount ??
          inst?.original_amount ??
          (paid ? paid + discount - fine : 0)
        );

        return {
          ...item,
          installmentName: description,
          description,
          feeAmount,
          discount,
          discount_amount: discount,
          fine,
          fine_amount: fine,
          amount: paid,
          paid_amount: paid,
          paying: paid,
        };
      });
    };

    // If receipt already contains items array, use it directly
    if (receiptRow?.items && receiptRow.items.length > 0) {
      const enriched = enrichItems(receiptRow.items);
      setReceiptData({
        ...receiptRow,
        items: enriched,
        itemsDetails: enriched,
        studentName: receiptRow.student ? undefined : studentName,
        studentId: admissionNo,
        admissionNo,
        fatherName,
        className,
        sectionName,
        collectedBy: user?.name || user?.full_name || 'Admin',
      });
      return;
    }

    // Fetch single receipt details via GET /fee-receipts/{receipt_id}?school_id={school_id}
    if (schoolId && receiptRow?.id) {
      const toastId = toast.loading('Fetching receipt details...');
      try {
        const fullReceipt = await feeReceiptsApi.getReceiptById(schoolId, receiptRow.id);
        toast.dismiss(toastId);
        const rData = fullReceipt?.data || fullReceipt;
        const enriched = enrichItems(rData?.items || receiptRow?.items || []);
        setReceiptData({
          ...rData,
          items: enriched,
          itemsDetails: enriched,
          studentName: (rData?.student || receiptRow.student) ? undefined : studentName,
          studentId: admissionNo,
          admissionNo,
          fatherName,
          className,
          sectionName,
          collectedBy: user?.name || user?.full_name || 'Admin',
        });
      } catch (err) {
        console.error('Failed to fetch receipt details:', err);
        toast.error('Failed to load receipt details', { id: toastId });
        const enriched = enrichItems(receiptRow?.items || []);
        setReceiptData({
          ...receiptRow,
          items: enriched,
          itemsDetails: enriched,
          studentName,
          studentId: admissionNo,
          admissionNo,
          fatherName,
          className,
          sectionName,
          collectedBy: user?.name || user?.full_name || 'Admin',
        });
      }
    }
  }, [schoolId, student, installments, user]);

  // ── Columns for Installments Table ───────────────────────────
  // ── Columns for Installments Table ───────────────────────────
  const installmentColumns = useMemo(() => [
    {
      field: 'installment_name',
      headerName: 'Installment',
      flex: 1.2,
      minWidth: 130,
      renderCell: (params) => {
        const feeTypeName = params.row.fee_type?.name;
        const instName = params.row.installment_name || 'Installment';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {feeTypeName && (
              <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ lineHeight: 1.2, mb: 0.25 }}>
                {feeTypeName}
              </Typography>
            )}
            <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.2 }}>
              {instName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 85,
      renderCell: (params) => {
        const displayStatus = (params.value || params.row.status || 'PENDING').toUpperCase();
        return (
          <Chip
            label={displayStatus}
            size="small"
            color={getStatusColor(displayStatus)}
            sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
          />
        );
      },
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      width: 105,
      renderCell: (params) => {
        const dt = params.row.due_date;
        return <Typography variant="body2" noWrap>{dt ? dayjs(dt).format('DD MMM YYYY') : '-'}</Typography>;
      },
    },
    {
      field: 'original_amount',
      headerName: 'Original (₹)',
      width: 95,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'discount_amount',
      headerName: 'Discount (₹)',
      width: 90,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value > 0 ? 'success.main' : 'text.secondary'} noWrap>
          {params.value > 0 ? `-${formatCurrency(params.value)}` : '₹0'}
        </Typography>
      ),
    },
    {
      field: 'fine_amount',
      headerName: 'Fine (₹)',
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value > 0 ? 'error.main' : 'text.secondary'} noWrap>
          {params.value > 0 ? `+${formatCurrency(params.value)}` : '₹0'}
        </Typography>
      ),
    },
    {
      field: 'paid_amount',
      headerName: 'Paid (₹)',
      width: 95,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="success.main" noWrap>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'balance_amount',
      headerName: 'Balance (₹)',
      width: 95,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} color={params.value > 0 ? 'error.main' : 'text.secondary'} noWrap>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'payable_amount',
      headerName: 'Payable (₹)',
      width: 95,
      renderCell: (params) => {
        const payVal = params.value || (params.row.original_amount - (params.row.discount_amount || 0) + (params.row.fine_amount || 0));
        return (
          <Typography variant="body2" fontWeight={600} noWrap>
            {formatCurrency(payVal)}
          </Typography>
        );
      },
    },
    {
      field: 'paid_on',
      headerName: 'Paid On',
      width: 105,
      renderCell: (params) => {
        const dt = params.row.paid_on || (params.row.status === 'PAID' ? params.row.updated_at : null);
        return (
          <Typography variant="body2" color="text.secondary" noWrap>
            {dt ? dayjs(dt).format('DD MMM YYYY') : '-'}
          </Typography>
        );
      },
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: 0.8,
      minWidth: 90,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value || '-'}
        </Typography>
      ),
    },
  ], []);

  // ── Columns for Receipts Table ───────────────────────────────
  const receiptColumns = useMemo(() => [
    {
      field: 'receipt_no',
      headerName: 'Receipt No.',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} color="primary.main" noWrap>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'receipt_date',
      headerName: 'Date',
      width: 105,
      renderCell: (params) => {
        const dt = params.row.receipt_date || params.row.created_at;
        return <Typography variant="body2" noWrap>{dt ? dayjs(dt).format('DD MMM YYYY') : '-'}</Typography>;
      },
    },
    {
      field: 'payment_mode',
      headerName: 'Payment Mode',
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || 'Cash'}
        </Typography>
      ),
    },
    {
      field: 'paid_amount',
      headerName: 'Paid (₹)',
      width: 95,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="success.main" noWrap>
          {formatCurrency(params.value || params.row.total_amount)}
        </Typography>
      ),
    },
    {
      field: 'discount_amount',
      headerName: 'Discount (₹)',
      width: 90,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value > 0 ? 'success.main' : 'text.secondary'} noWrap>
          {params.value > 0 ? `-${formatCurrency(params.value)}` : '₹0'}
        </Typography>
      ),
    },
    {
      field: 'fine_amount',
      headerName: 'Fine (₹)',
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value > 0 ? 'error.main' : 'text.secondary'} noWrap>
          {params.value > 0 ? `+${formatCurrency(params.value)}` : '₹0'}
        </Typography>
      ),
    },
    {
      field: 'total_amount',
      headerName: 'Total (₹)',
      width: 95,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} color="primary.main" noWrap>
          {formatCurrency(params.value || params.row.paid_amount)}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 85,
      renderCell: (params) => {
        const cancelled = params.row.is_cancelled || params.row.status === 'CANCELLED';
        return (
          <Chip
            label={cancelled ? 'Cancelled' : (params.row.status || 'Paid')}
            size="small"
            color={cancelled ? 'error' : 'success'}
            sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
          />
        );
      },
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: 0.8,
      minWidth: 90,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 155,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isCancelled = params.row.is_cancelled || params.row.status === 'CANCELLED';
        return (
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', justifyContent: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<Eye size={13} />}
              onClick={(e) => {
                e.stopPropagation();
                handlePreviewReceipt(params.row);
              }}
              sx={{
                px: 1,
                py: 0.25,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 1.5,
                height: 26,
              }}
            >
              View
            </Button>
            {!isCancelled && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<Ban size={13} />}
                onClick={(e) => {
                  e.stopPropagation();
                  setCancelReceiptRow(params.row);
                }}
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  height: 26,
                }}
              >
                Cancel
              </Button>
            )}
          </Box>
        );
      },
    },
  ], [handlePreviewReceipt]);

  // ── Loading & error states ───────────────────────────────────
  if (isLoadingStudent) {
    return (
      <Box sx={{ pb: 6 }}>
        <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!student) {
    return (
      <Box sx={{ pb: 6 }}>
        <Alert severity="error">Student not found.</Alert>
        <Button startIcon={<ArrowLeft />} onClick={() => navigate('/fees/collect')} sx={{ mt: 2 }}>
          Back to Directory
        </Button>
      </Box>
    );
  }

  const stu = student?.student || student;
  const parent = student?.parent || stu?.parent || {};
  const enrollment = student?.enrollment || stu?.enrollment || {};
  const studentName = `${stu?.first_name || ''} ${stu?.last_name || ''}`.trim() || stu?.student_name || 'Student';

  const admissionNo = stu?.admission_no || stu?.admission_number || '';
  const classNameRaw = enrollment?.class?.class_name || enrollment?.class?.name || stu?.class?.name || stu?.class?.class_name || stu?.class_name || '';
  const className = classNameRaw.toLowerCase().startsWith('class') ? classNameRaw : classNameRaw ? `Class ${classNameRaw}` : '';
  const sectionName = enrollment?.section?.section_name || enrollment?.section?.name || stu?.section?.name || stu?.section?.section_name || stu?.section_name || '';
  const classSectionStr = `${className} ${sectionName}`.trim();
  const fatherName = parent?.father_name || parent?.guardian_name || stu?.father_name || '-';
  const mobile = parent?.father_mobile || parent?.guardian_mobile || stu?.mobile || '-';

  return (
    <Box sx={{ pb: 6 }}>
      {/* ── Page Header ───────────────────────────────────────── */}
      <PageHeader
        title={`Fee Status - ${studentName}`}
        subtitle={`${admissionNo} | ${classSectionStr} | Father: ${fatherName} | Mobile: ${mobile}`}
        action={
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/fees/collect')}
          >
            Back to Directory
          </Button>
        }
      />

      {/* ── Fee Summary Stats ─────────────────────────────────── */}
      <Box
        sx={{
          mt: 3,
          p: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${tokens.colors.primary.main}08, ${tokens.colors.primary.main}14)`,
          border: `1px solid ${tokens.colors.primary.main}20`,
        }}
      >
        {isLoadingSummary ? (
          <Box sx={{ display: 'flex', gap: 2 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" width="33%" height={80} sx={{ borderRadius: 2 }} />
            ))}
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 160,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Fee
                </Typography>
                <Typography variant="h6" fontWeight={800}>
                  {formatCurrency(feeSummary?.total_fee || 0)}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 160,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Paid
                </Typography>
                <Typography variant="h6" fontWeight={800} color="success.main">
                  {formatCurrency(feeSummary?.paid || 0)}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 160,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Balance Due
                </Typography>
                <Typography variant="h6" fontWeight={800} color="error.main">
                  {formatCurrency(feeSummary?.balance || 0)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LinearProgress
                variant="determinate"
                value={paymentProgress}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: paymentProgress === 100 ? 'success.main' : 'primary.main',
                    borderRadius: 4,
                  },
                }}
              />
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                {paymentProgress}% Paid
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Tabs & Tables Component ───────────────────────────── */}
      <WidgetContainer sx={{ mt: 3, p: 0, overflow: 'hidden' }}>
        <Tabs
          value={statusTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: `1px solid ${tokens.colors.border}`,
            px: 3,
            pt: 1,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 },
          }}
        >
          <Tab
            label="Installments Schedule"
            icon={<CalendarDays size={16} />}
            iconPosition="start"
            sx={{ gap: 0.8 }}
          />
          <Tab
            label="Payment History"
            icon={<Receipt size={16} />}
            iconPosition="start"
            sx={{ gap: 0.8 }}
          />
        </Tabs>

        {/* Tab 0: Installments Schedule Table */}
        {statusTab === 0 && (
          <Box sx={{ p: 3 }}>
            {isLoadingInstallments ? (
              <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 2 }} />
            ) : installments.length === 0 ? (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                No fee installments found for this student. Please ensure fees are assigned first.
              </Alert>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  autoHeight
                  rows={installments}
                  rowHeight={52}
                  disableRowSelectionOnClick
                  pageSizeOptions={[10, 25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10, page: 0 } },
                  }}
                  getRowId={(row) => row.id}
                  columns={installmentColumns}
                  slots={{ toolbar: CustomToolbar }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Tab 1: Payment History Table */}
        {statusTab === 1 && (
          <Box sx={{ p: 3 }}>
            {isLoadingReceipts ? (
              <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 2 }} />
            ) : receipts.length === 0 ? (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                No payment receipts found for this student yet.
              </Alert>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  autoHeight
                  rows={receipts}
                  rowHeight={52}
                  disableRowSelectionOnClick
                  pageSizeOptions={[10, 25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10, page: 0 } },
                  }}
                  getRowId={(row) => row.id}
                  columns={receiptColumns}
                  slots={{ toolbar: CustomToolbar }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </WidgetContainer>

      {/* ── Receipt Preview Modal ─────────────────────────────── */}
      <ReceiptModal
        open={!!receiptData}
        onClose={() => setReceiptData(null)}
        receipt={receiptData}
        schoolInfo={currentSchool}
      />

      {/* ── Cancel Receipt Confirmation Dialog ─────────────────── */}
      <Dialog
        open={Boolean(cancelReceiptRow)}
        onClose={() => !isCancelling && setCancelReceiptRow(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            p: 0.5,
            boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, pt: 1.5, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={700} color="error.main">
            Cancel Fee Receipt
          </Typography>
          <IconButton size="small" onClick={() => setCancelReceiptRow(null)} disabled={isCancelling}>
            <X size={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 1, px: 2 }}>
          {cancelReceiptRow && (
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to cancel fee receipt <strong>{cancelReceiptRow.receipt_no || cancelReceiptRow.receipt_number || cancelReceiptRow.id}</strong>?
              <br /><br />
              This will reverse the paid amount of <strong>₹{(cancelReceiptRow.paid_amount || cancelReceiptRow.total_amount || 0).toLocaleString()}</strong> and restore installment balances.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setCancelReceiptRow(null)}
            disabled={isCancelling}
            sx={{ borderRadius: 1.5, px: 2, py: 0.5, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            Close
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={handleConfirmCancelReceipt}
            disabled={isCancelling}
            sx={{ borderRadius: 1.5, fontWeight: 600, px: 2.5, py: 0.5, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            {isCancelling ? 'Cancelling...' : 'Confirm Cancel Receipt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Utility: Number to Words (INR) ────────────────────────────
function numberToWordsINR(num) {
  if (!num || num === 0) return 'Zero';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000)
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convert(n % 100) : '');
    if (n < 100000)
      return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000)
      return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };

  return 'Rupees ' + convert(Math.floor(num));
}
